import { useState } from "react"
import { Grid, IconButton, Box, Card, CardHeader, CardContent, Tooltip } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import ConfirmDialog from "components/common/confirm-dialog"
import AlertDialog from "components/common/alert-dialog"
import { GridContainer, InfoText } from "components/common-styles"
import { useStores } from "stores/root-store"
import { twoDecimalPlace } from "utils/app-helper"
import { formatStandardDate } from "utils/date-helper"
import { useNavigate } from "react-router-dom"
import StatusComponent from "components/common/status-component"
import { FileDownload } from "@mui/icons-material"
import { trackEvent } from "utils/analytics"

const InboundDeliveryInfo = ({ onEditClicked, onPrintClicked }) => {
  const { InboundDeliveryStore } = useStores()
  const navigate = useNavigate()

  const deliveryInfo = { ...InboundDeliveryStore.deliveryInfo }
  const products = [...InboundDeliveryStore.products]

  const [openDelete, setOpenDelete] = useState(false)
  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")

  const onDeleteClicked = () => {
    setOpenDelete(true)
  }

  const onConfirmDelete = async () => {
    const deleted = await InboundDeliveryStore.deleteData()
    trackEvent("record_delete", { entity: "inbound_delivery", count: 1, bulk: "false", result: deleted ? "success" : "failed" })
    if (deleted) {
      navigate("/inbound-delivery")
    } else {
      setAlertContent("This inbound delivery could not be deleted. Please try again.")
      setOpenAlert(true)
    }
  }

  return (
    <Card>
      <CardHeader
        title="Delivery Information"
        action={
          deliveryInfo?.receipt_status == "Open" && (
            <Box>
              <Tooltip placement="top" title="Download PaperWork">
                <IconButton id="btnDownloadPaperWork" size="large" onClick={onPrintClicked}>
                  <FileDownload fontSize="inherit" />
                </IconButton>
              </Tooltip>

              <Tooltip placement="top" title="Edit">
                <IconButton id="btnEdit" size="large" onClick={onEditClicked}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              <Tooltip placement="top" title="Delete">
                <IconButton id="btnDelete" size="large" onClick={onDeleteClicked}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              <ConfirmDialog open={openDelete} onClose={() => setOpenDelete(false)} onConfirm={onConfirmDelete} />
              <AlertDialog open={openAlert} content={alertContent} onClose={() => setOpenAlert(false)} />
            </Box>
          )
        }
      />
      <CardContent>
        <GridContainer container spacing={3}>
          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Warehouse
            </Grid>
            <Grid item xs={8}>
              <InfoText id="warehouse">{deliveryInfo?.warehouse}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Total Qty
            </Grid>
            <Grid item xs={8}>
              <InfoText>{twoDecimalPlace(deliveryInfo?.total_quantity)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Receipt ID
            </Grid>
            <Grid item xs={8}>
              <InfoText id="receipt_id">{deliveryInfo?.receipt_id}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Total Volume
            </Grid>
            <Grid item xs={8}>
              <InfoText id="total_volume">{twoDecimalPlace(deliveryInfo?.total_volume)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Receipt Type
            </Grid>
            <Grid item xs={8}>
              <InfoText id="receipt_type">{deliveryInfo?.receipt_type}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Total Weight
            </Grid>
            <Grid item xs={8}>
              <InfoText>{twoDecimalPlace(deliveryInfo?.total_weight)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Receipt Status
            </Grid>
            <Grid item xs={8}>
              <StatusComponent id="receipt_status" status={deliveryInfo?.receipt_status} />
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Total Lines
            </Grid>
            <Grid item xs={8}>
              <InfoText>{twoDecimalPlace(deliveryInfo?.total_lines)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Tracking Number
            </Grid>
            <Grid item xs={8}>
              <InfoText id="trailer_id">{deliveryInfo?.trailer_id}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Creation Date
            </Grid>
            <Grid item xs={8}>
              <InfoText>{formatStandardDate(deliveryInfo?.creation_date)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              ERP Number
            </Grid>
            <Grid item xs={8}>
              <InfoText id="erp_order">{deliveryInfo?.erp_order}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Scheduled Date
            </Grid>
            <Grid item xs={8}>
              <InfoText id="scheduled_date">{formatStandardDate(deliveryInfo?.scheduled_date)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Supplier
            </Grid>
            <Grid item xs={8}>
              <InfoText id="source">{deliveryInfo?.source}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Last Update Date
            </Grid>
            <Grid item xs={8}>
              <InfoText>{formatStandardDate(deliveryInfo?.last_update_datetime)}</InfoText>
            </Grid>
          </Grid>

          <Grid item container md={5} xs={12}>
            <Grid item xs={4}>
              Receipt Variance
            </Grid>
            <Grid item xs={8}>
              <InfoText>{products?.reduce((variance, product) => variance + parseInt(product.receipted_quantity - product.expected_quantity, 10), 0)}</InfoText>
            </Grid>
          </Grid>
          <Grid item container md={7} xs={12}>
            <Grid item xs={4}>
              Close Date
            </Grid>
            <Grid item xs={8}>
              <InfoText>{formatStandardDate(deliveryInfo?.close_date)}</InfoText>
            </Grid>
          </Grid>
        </GridContainer>
      </CardContent>
    </Card>
  )
}

export default InboundDeliveryInfo
